import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import type { ReportResult } from './hooks/useReports';
import { formatReportCellValue } from './reportResultsUtils';

interface LegacyReportResultsTableProps {
  result: ReportResult;
  page: number;
  onPageChange: (page: number) => void;
  embedded?: boolean;
}

export function LegacyReportResultsTable({
  result,
  page,
  onPageChange,
  embedded = false,
}: LegacyReportResultsTableProps) {
  const visibleColumns = result.columns.filter((col) => col.visible);
  const totalPages = Math.ceil(result.total / result.pageSize);

  const tableBody = (
    <>
      {result.summary && Object.keys(result.summary).length > 0 && (
        <div className="mb-4 flex flex-wrap gap-4 rounded-lg bg-gray-50 p-3 dark:bg-gray-800/50">
          {Object.entries(result.summary).map(([key, value]) => (
            <div key={key} className="text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </p>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {typeof value === 'number'
                  ? value.toLocaleString('en-CA', { minimumFractionDigits: 2 })
                  : value}
              </p>
            </div>
          ))}
        </div>
      )}

      {result.rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
          No results found for the selected filters
        </p>
      ) : (
        <div
          className={
            embedded
              ? 'max-h-[min(60vh,520px)] overflow-auto rounded-lg border border-border dark:border-border-dark'
              : 'overflow-x-auto'
          }
        >
          <table className="w-full text-left text-sm" role="grid">
            <thead
              className={embedded ? 'sticky top-0 bg-gray-50 dark:bg-surface-dark-tertiary' : ''}
            >
              <tr className="border-b border-border dark:border-border-dark">
                {visibleColumns.map((col) => (
                  <th
                    key={col.id}
                    className="px-4 py-3 font-medium text-gray-600 dark:text-gray-400"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border dark:divide-border-dark">
              {result.rows.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className="transition-colors hover:bg-surface-secondary dark:hover:bg-surface-dark-tertiary"
                >
                  {visibleColumns.map((col) => (
                    <td key={col.id} className="px-4 py-3 text-gray-900 dark:text-gray-100">
                      {formatReportCellValue(row[col.id])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between border-t border-border pt-4 dark:border-border-dark">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Page {page} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </>
  );

  if (embedded) {
    return <div>{tableBody}</div>;
  }

  return (
    <Card>
      <CardHeader
        title="Report Results"
        description={`Showing ${result.rows.length} of ${result.total} rows`}
      />
      <CardContent>{tableBody}</CardContent>
    </Card>
  );
}
